
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { GripVertical, Save, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FeatureItem } from "@/types/feature";

const FeatureOrderManagement = () => {
  const { toast } = useToast();
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  // Fetch all features (enabled and disabled) for admin management
  const { data: fetchedFeatures, isLoading: queryLoading } = useQuery({
    queryKey: ['admin_feature_items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_items')
        .select('*')
        .order('order_index');
      
      if (error) {
        console.error('Error fetching feature items:', error);
        throw error;
      }
      
      return data as FeatureItem[];
    }
  });

  useEffect(() => {
    if (fetchedFeatures) {
      setFeatures(fetchedFeatures);
      setIsLoading(false);
    }
  }, [fetchedFeatures]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(features);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order_index for all items
    const updatedItems = items.map((item, index) => ({
      ...item,
      order_index: index
    }));

    setFeatures(updatedItems);
  };

  const handleToggleFeature = (featureId: string) => {
    setFeatures(prev => prev.map(feature => 
      feature.id === featureId 
        ? { ...feature, is_enabled: !feature.is_enabled }
        : feature
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update all features in the database
      const updates = features.map(feature => ({
        id: feature.id,
        order_index: feature.order_index,
        is_enabled: feature.is_enabled,
        updated_at: new Date().toISOString()
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('feature_items')
          .update({
            order_index: update.order_index,
            is_enabled: update.is_enabled,
            updated_at: update.updated_at
          })
          .eq('id', update.id);

        if (error) {
          console.error('Error updating feature:', error);
          throw error;
        }
      }

      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['feature_items'] });
      await queryClient.invalidateQueries({ queryKey: ['admin_feature_items'] });

      toast({
        title: "Success",
        description: "Feature order and visibility updated successfully.",
      });
    } catch (error) {
      console.error('Error saving feature order:', error);
      toast({
        title: "Error",
        description: "Failed to save feature order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      // Refresh from database
      const { data, error } = await supabase
        .from('feature_items')
        .select('*')
        .order('order_index');

      if (error) throw error;

      setFeatures(data);
      toast({
        title: "Reset",
        description: "Changes have been reset to the last saved state.",
      });
    } catch (error) {
      console.error('Error resetting:', error);
      toast({
        title: "Error",
        description: "Failed to reset changes.",
        variant: "destructive",
      });
    }
  };

  if (isLoading || queryLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Feature Order Management</CardTitle>
          <p className="text-sm text-gray-600">
            Drag and drop to reorder features in the navigation dropdown. Toggle to enable/disable features.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="features">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {features.map((feature, index) => (
                    <Draggable key={feature.id} draggableId={feature.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-4 p-4 bg-white border rounded-lg ${
                            snapshot.isDragging ? 'shadow-lg' : 'shadow-sm'
                          } ${!feature.is_enabled ? 'opacity-60' : ''}`}
                        >
                          <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                            <GripVertical className="h-5 w-5 text-gray-400" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{feature.label}</span>
                              <span className="text-sm text-gray-500">({feature.icon_name})</span>
                            </div>
                            <div className="text-sm text-gray-500">{feature.href}</div>
                          </div>

                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={feature.is_enabled}
                              onChange={() => handleToggleFeature(feature.id)}
                              className="rounded"
                            />
                            <span className="text-sm">Enabled</span>
                          </label>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={isSaving}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureOrderManagement;
